import Div100vh from 'react-div-100vh';
import {
  borderWidths,
  colors,
  gap,
  layers,
  media,
  opacity,
  RoundButton,
  shadows,
  spacing,
} from '@lamimed/pantheon';
import Link from 'next/link';
import { math, rem, rgba } from 'polished';
import styled, { css } from 'styled-components';

export const backButtonIconSize = 24;

export const LogoContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

type LogoProps = {
  marginBottom?: keyof typeof spacing;
};

export const Logo = styled(Link)<LogoProps>`
  ${({ marginBottom = 'none' }) => css`
    padding: ${spacing.medium};
    margin-bottom: ${spacing[marginBottom]};

    ${media.greaterThan('desktop')`
      padding: ${spacing.none};
    `}
  `}
`;

export const navigationWidth = '26rem';
export const navigationHeight = '4.4rem';

export const Container = styled(Div100vh)`
  position: fixed;
  top: 0;
  height: ${navigationHeight};
  z-index: ${layers.menu};
  width: 100%;
  grid-area: nav;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing.medium} 0;
  box-shadow: ${shadows.level1};
  background-color: ${colors.light};
  border-bottom: ${borderWidths.thin} solid ${colors.neutral_light};

  ${media.lessThan('desktop')`
    height: ${navigationHeight} !important;
  `};

  ${media.greaterThan('desktop')`
    position: initial;
    z-index: initial;
    width: ${navigationWidth};
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    box-shadow: none;
    padding: ${spacing.medium};
    border-right: ${borderWidths.small} solid ${colors.neutral_light};
  `};
`;

export const Menu = styled.div`
  display: flex;
  flex-direction: column;

  ${gap(spacing.small)}
`;

type MenuItemProps = {
  active?: boolean;
  open?: boolean;
};

const activeStyle = css`
  background-color: ${colors.neutral_light};
  color: ${colors.dark};

  p {
    color: ${colors.dark};
  }
`;

export const MenuItem = styled.button<MenuItemProps>`
  ${({ open = false, active = false }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    border-radius: 0.5rem;
    background-color: transparent;
    border: none;
    outline: none;
    width: 100%;
    cursor: pointer;

    padding: 0 ${spacing.small};
    color: ${rgba(colors.text__primary, opacity.medium)};
    margin-top: ${open ? spacing.small : spacing.none};

    p {
      color: ${rgba(colors.text__primary, opacity.medium)};
    }

    ${active && activeStyle};

    &:hover {
      ${activeStyle}
    }
  `};
`;

export const MenuItemContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0.4rem 0;

  p {
    font-size: 1.4rem;
  }

  ${gap(spacing.small)};
`;

export const MenuContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const MenuButton = styled(RoundButton).attrs({ variant: 'transparent' })`
  && {
    min-width: initial;
    height: initial;
  }
`;

export const BackButtonContainer = styled.div`
  min-width: ${math(
    `${rem(backButtonIconSize, 10)} + (${spacing.medium} * 2)`,
  )};
`;

export const BackButton = styled(RoundButton).attrs({ variant: 'transparent' })`
  && {
    min-width: unset;
    height: initial;
  }
`;

type AgendaProps = {
  open: boolean;
};

export const AgendaSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  p {
    font-size: 1.2rem;
  }
`;

export const AgendaDetails = styled.div<AgendaProps>`
  ${({ open }) => css`
    cursor: pointer;
    user-select: none;
    -webkit-tap-highlight-color: transparent;

    & ${AgendaSummary} ~ * {
      overflow: hidden;
      transition: max-height 0.3s;
      // NOTE: 3rem is just an arbitrary value to make the animation
      // work
      max-height: ${open ? '3rem' : '0'};
    }
  `};
`;
